import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserForm } from 'components/Users/UserForm'
import { UserAccountSchema, UserDetailsSchema } from 'components/Users/validationSchema'
import { SuccessDialog } from 'components/Shared/Dialogs/SuccessDialog'
import { triggerFormSubmit } from 'utils/utils'
import { TitleBar } from 'components/Shared/CrudComponents/TitleBar'
import { USERNAME_CONFLICT } from 'services/errorCodes'
import { createUser } from 'services'
import { HTTP_422_ERROR } from 'Constants'
import ApiError from 'constants/ApiError'
import { useErrorHandling } from 'context/errorHandling'
import { useNavigatePrevent } from 'context/navigatePrevent'
import { FormikForm } from 'components/Shared/FormikForm'
import Module from 'constants/customFieldModules'
import styles from 'components/Shared/CrudComponents/SimpleForm.module.css'
import { UserAccountFormFields } from '../UserAccountFormFields'
import { useAuth, USER_ACCOUNT_WRITE } from 'context/auth'
import { addGroupMember } from 'services/GroupsService'
import { EXTRA, MAIN } from 'components/Groups/GroupsForm/MembershipType'

const initialValues = {
  username: '',
  password: '',
  passwordConfirmation: '',
  role: null,
  firstName: '',
  lastName: '',
  phoneNumber: '',
  email: '',
  personalPhoneNumber: '',
  hash: '',
  mainGroupId: null,
  extraGroupsIds: []
}

export function CreateUserForm () {
  const [createdDialogOpen, setCreatedDialogOpen] = useState(false)

  const { t } = useTranslation()
  const { handleError } = useErrorHandling()
  const { hasAnyPrivileges } = useAuth()

  const createUserFormRef = useRef()

  const { setNavigationUnlocked } = useNavigatePrevent()

  function handleSubmit ({ passwordConfirmation, mainGroupId, extraGroupsIds, ...values }, onSuccess) {
    createUser({ ...values })
      .then(createdUser => onSuccess(createdUser.id)
        .then(() => Promise.resolve(createdUser)))
      .then(createdUser => Promise.all(
          [
            mainGroupId ? addGroupMember(mainGroupId, {
              userId: createdUser.id,
              membershipType: MAIN
            }) : Promise.resolve(),
            ...extraGroupsIds.map(groupId => addGroupMember(groupId, {
              userId: createdUser.id,
              membershipType: EXTRA
            }))]
        )
      )
      .then(() => setCreatedDialogOpen(true))
      .catch(error => handleError(error, {
        [HTTP_422_ERROR]: error?.response?.data?.errorCode === USERNAME_CONFLICT ?
          'notifications.error.usernameConflict' : ApiError.UnprocessableEntity
      }))
  }

  const bottomFormContent = () =>
    <div className={styles.container}>
      <h2>{t('form.account.title')}</h2>
      <UserAccountFormFields disabled={!hasAnyPrivileges([USER_ACCOUNT_WRITE])}/>
    </div>

  return <div>
    <TitleBar
      buttonLabel={t('user.form.submit')}
      onButtonClick={triggerFormSubmit(createUserFormRef)}
      title={t('user.form.createTitle')}/>
    <SuccessDialog open={createdDialogOpen}
                   onClose={() => {
                     setCreatedDialogOpen(false)
                     setNavigationUnlocked(history => history.push('/users'))
                   }} content={'notifications.success.createdUser'}/>
    <FormikForm
      innerRef={createUserFormRef}
      initialValues={initialValues}
      validationSchema={UserAccountSchema.concat(UserDetailsSchema)}
      onSubmit={handleSubmit}
      customFields={{
        module: Module.Users
      }}>
      <UserForm bottomContent={bottomFormContent}/>
    </FormikForm>
  </div>
}
